import { MetricRangeType } from "@waterly/graphql-types";

import type {
  BuildRangeExpressionInput,
  LimitRangeSide,
  MetricRangeCardMetricRange,
} from "./metricRangeUtils";

const safeMinValue = (value: number | undefined | null) => value ?? -Infinity;
const safeMaxValue = (value: number | undefined | null) => value ?? Infinity;

export const calculateRangeMinMax = ({
  previousRange,
  range,
  nextRange,
  side,
  isPreviousRangeSingleton,
  isNextRangeSingleton,
  isPreviousRangeIdeal,
  isNextRangeIdeal,
}: {
  previousRange?: MetricRangeCardMetricRange;
  range: MetricRangeCardMetricRange;
  nextRange?: MetricRangeCardMetricRange;
  side: LimitRangeSide;
  isPreviousRangeSingleton: boolean;
  isNextRangeSingleton: boolean;
  isPreviousRangeIdeal: boolean;
  isNextRangeIdeal: boolean;
}): Pick<BuildRangeExpressionInput, "rangeMin" | "rangeMax"> => {
  const isLowerRange = side === MetricRangeType.Lower;

  if (range.ideal) {
    return {
      rangeMin: safeMinValue(isLowerRange ? previousRange?.rangeMin : range.rangeMin),
      rangeMax: safeMaxValue(isLowerRange ? range.rangeMax : nextRange?.rangeMax),
    };
  }

  const rangeMin = isLowerRange ? previousRange?.rangeMax : previousRange?.rangeMin;
  const rangeMax = isLowerRange ? nextRange?.rangeMax : nextRange?.rangeMin;

  return {
    rangeMin: safeMinValue(isPreviousRangeSingleton || isPreviousRangeIdeal ? range.rangeMin : rangeMin),
    rangeMax: safeMaxValue(isNextRangeSingleton || isNextRangeIdeal ? range.rangeMax : rangeMax),
  };
};

type CalculateInclusivityInput = {
  side: LimitRangeSide;
  isPreviousRangeSingleton: boolean;
  isNextRangeSingleton: boolean;
  isPreviousRangeBorderingRangeSingleton: boolean;
  isNextRangeBorderingRangeSingleton: boolean;
  isPreviousRangeIdeal: boolean;
  isNextRangeIdeal: boolean;
  isPreviousRangeBorderingRangeIdeal: boolean;
  isNextRangeBorderingRangeIdeal: boolean;
  ideal: boolean;
};

export const calculateInclusivity = ({
  side,
  isPreviousRangeSingleton,
  isNextRangeSingleton,
  isPreviousRangeBorderingRangeSingleton,
  isNextRangeBorderingRangeSingleton,
  isPreviousRangeIdeal,
  isNextRangeIdeal,
  isPreviousRangeBorderingRangeIdeal,
  isNextRangeBorderingRangeIdeal,
  ideal,
}: CalculateInclusivityInput): Pick<BuildRangeExpressionInput, "rangeMinInclusive" | "rangeMaxInclusive"> => {
  const isLowerRange = side === MetricRangeType.Lower;

  if (ideal) {
    return {
      rangeMinInclusive: !isLowerRange,
      rangeMaxInclusive: isLowerRange,
    };
  }

  return {
    rangeMinInclusive:
      !isPreviousRangeSingleton &&
      !isPreviousRangeIdeal &&
      (isLowerRange || (!isPreviousRangeBorderingRangeSingleton && !isPreviousRangeBorderingRangeIdeal)),
    rangeMaxInclusive:
      !isNextRangeSingleton &&
      !isNextRangeIdeal &&
      (!isLowerRange || (!isNextRangeBorderingRangeSingleton && !isNextRangeBorderingRangeIdeal)),
  };
};
